import { useState, useEffect, useRef, useCallback } from 'react';
import { getCustomers } from '../services/api/customerApi';

/**
 * Custom hook for managing customer list data with pagination, sorting, and search
 * @returns {Object} Customer list state and controls
 */
export const useCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [augmentationFilter, setAugmentationFilter] = useState('all');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [sortModel, setSortModel] = useState([
    { field: 'customerName', sort: 'asc' }
  ]);

  // Use ref to prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);
  const debounceTimer = useRef(null);

  // Debounce search term (500ms delay)
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const sortField = sortModel.length > 0 ? sortModel[0].field : 'customerName';
      const sortDescending = sortModel.length > 0 ? sortModel[0].sort === 'desc' : false;

      const params = {
        pageNumber: paginationModel.page + 1, // API uses 1-based pagination
        pageSize: paginationModel.pageSize,
        searchTerm: debouncedSearchTerm,
        sortBy: sortField,
        sortDescending: sortDescending
      };

      const response = await getCustomers(params);
      let customerList = response.data.items || response.data || [];

      // Apply client-side augmentation filter
      if (augmentationFilter !== 'all') {
        const filterValue = augmentationFilter === 'enabled';
        customerList = customerList.filter(c => c.augmentationEnabled === filterValue);
      }

      setCustomers(customerList);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch customers');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, paginationModel, sortModel, augmentationFilter]);

  // Fetch customers when dependencies change
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
    }
    fetchCustomers();
  }, [fetchCustomers]);

  const refreshCustomers = useCallback(async () => {
    await fetchCustomers();
  }, [fetchCustomers]);

  return {
    customers,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    augmentationFilter,
    setAugmentationFilter,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    refreshCustomers
  };
};
